<script>
  import { onMount } from 'svelte';
  import Editor from '@tinymce/tinymce-svelte';

  const tinyMceApiKey = 'usvl1m5wsho8d78ixib2ladkg9cca2oonqln4so3wls7ujj5';
  const API_BASE_URL = '/studentplayground';

  // Form state
  let topic = '';
  let content = '';
  let displayOrder = 0;
  let isPublished = false;
  let isFeatured = false;
  let showToTeachers = true;
  let showToStudents = true;

  // Component state
  let sections = [];
  let loading = false;
  let errorMessage = '';
  let successMessage = '';
  let editingId = null;
  let isSubmitting = false;

  // Fetch all sections
  async function fetchSections() {
    loading = true;
    errorMessage = '';
    const token = localStorage.getItem('jwtToken');
    
    if (!token) {
      errorMessage = 'JWT token not found. Please log in.';
      loading = false;
      return;
    }

    try {
      const response = await fetch(`${API_BASE_URL}/api/information-sections/`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (response.ok) {
        const data = await response.json();
        sections = data.sections || [];
      } else {
        const errorData = await response.json().catch(() => ({}));
        errorMessage = errorData.error || `Failed to fetch sections: ${response.status}`;
      }
    } catch (error) {
      errorMessage = `Network error: ${error.message}`;
    } finally {
      loading = false;
    }
  }

  // Load section for editing
  function loadSectionForEdit(section) {
    editingId = section.id;
    topic = section.topic;
    content = section.content || '';
    displayOrder = section.display_order || 0;
    isPublished = section.is_published || false;
    isFeatured = section.is_featured || false;
    showToTeachers = section.show_to_teachers !== undefined ? section.show_to_teachers : true;
    showToStudents = section.show_to_students !== undefined ? section.show_to_students : true;
    
    // Scroll to form
    document.querySelector('.section-form')?.scrollIntoView({ behavior: 'smooth' });
  }

  // Reset form
  function resetForm() {
    editingId = null;
    topic = '';
    content = '';
    displayOrder = 0;
    isPublished = false;
    isFeatured = false;
    showToTeachers = true;
    showToStudents = true;
    errorMessage = '';
    successMessage = '';
  }

  // Submit form (create or update)
  async function submitForm() {
    if (!topic.trim()) {
      errorMessage = 'Topic is required';
      return;
    }

    if (!content.trim()) {
      errorMessage = 'Content is required';
      return;
    }

    isSubmitting = true;
    errorMessage = '';
    successMessage = '';

    const token = localStorage.getItem('jwtToken');
    if (!token) {
      errorMessage = 'JWT token not found. Please log in.';
      isSubmitting = false;
      return;
    }

    const payload = {
      topic: topic.trim(),
      content: content,
      display_order: displayOrder,
      is_published: isPublished,
      is_featured: isFeatured,
      show_to_teachers: showToTeachers,
      show_to_students: showToStudents,
    };

    try {
      const url = editingId 
        ? `${API_BASE_URL}/api/information-sections/${editingId}/update/`
        : `${API_BASE_URL}/api/information-sections/create/`;
      
      const method = editingId ? 'PUT' : 'POST';

      const response = await fetch(url, {
        method: method,
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload),
      });

      if (response.ok) {
        successMessage = editingId ? 'Section updated successfully!' : 'Section created successfully!';
        resetForm();
        await fetchSections();
        
        setTimeout(() => {
          successMessage = '';
        }, 3000);
      } else {
        const errorData = await response.json().catch(() => ({}));
        errorMessage = errorData.error || `Failed to ${editingId ? 'update' : 'create'} section: ${response.status}`;
      }
    } catch (error) {
      errorMessage = `Network error: ${error.message}`;
    } finally {
      isSubmitting = false;
    }
  }

  // Delete section
  async function deleteSection(sectionId) {
    if (!confirm('Are you sure you want to delete this section?')) {
      return;
    }

    const token = localStorage.getItem('jwtToken');
    if (!token) {
      errorMessage = 'JWT token not found. Please log in.';
      return;
    }

    try {
      const response = await fetch(`${API_BASE_URL}/api/information-sections/${sectionId}/delete/`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (response.ok) {
        successMessage = 'Section deleted successfully!';
        await fetchSections();
        
        setTimeout(() => {
          successMessage = '';
        }, 3000);
      } else {
        const errorData = await response.json().catch(() => ({}));
        errorMessage = errorData.error || `Failed to delete section: ${response.status}`;
      }
    } catch (error) {
      errorMessage = `Network error: ${error.message}`;
    }
  }

  // TinyMCE image upload handler
  async function handleImageUpload(blobInfo, progress) {
    return new Promise(async (resolve, reject) => {
      const token = localStorage.getItem('jwtToken');
      if (!token) {
        reject('JWT token not found');
        return;
      }

      const formData = new FormData();
      formData.append('image', blobInfo.blob(), blobInfo.filename());

      try {
        const xhr = new XMLHttpRequest();
        
        xhr.upload.addEventListener('progress', (e) => {
          if (e.lengthComputable) {
            progress((e.loaded / e.total) * 100);
          }
        });

        xhr.addEventListener('load', () => {
          if (xhr.status === 200) {
            const response = JSON.parse(xhr.responseText);
            resolve(response.image_url);
          } else {
            reject('Image upload failed');
          }
        });

        xhr.addEventListener('error', () => {
          reject('Image upload failed');
        });

        xhr.open('POST', `${API_BASE_URL}/api/information-sections/upload-image/`);
        xhr.setRequestHeader('Authorization', `Bearer ${token}`);
        xhr.send(formData);
      } catch (error) {
        reject(error.message);
      }
    });
  }

  // Format date for display
  function formatDate(dateString) {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleDateString() + ' ' + date.toLocaleTimeString();
  }

  // Initialize on mount
  onMount(() => {
    fetchSections();
  });
</script>

<div class="information-section-component">
  <h2>Information Sections Management</h2>
  <p class="description">Create and manage information sections with rich text content and images.</p>

  <!-- Success/Error Messages -->
  {#if successMessage}
    <div class="message success">{successMessage}</div>
  {/if}

  {#if errorMessage}
    <div class="message error">{errorMessage}</div>
  {/if}

  <!-- Form Section -->
  <div class="section-form">
    <h3>{editingId ? 'Edit Section' : 'Create New Section'}</h3>
    
    <form on:submit|preventDefault={submitForm}>
      <div class="form-group">
        <label for="topic">Topic Name *</label>
        <input
          id="topic"
          type="text"
          bind:value={topic}
          placeholder="e.g., How to Book a Class, Payment Methods"
          required
        />
      </div>

      <div class="form-group">
        <label for="content">Content *</label>
        <Editor
          channel="7"
          bind:value={content}
          apiKey={tinyMceApiKey}
          conf={{
            height: 500,
            menubar: true,
            plugins: [
              'advlist autolink lists link image charmap preview anchor',
              'searchreplace visualblocks code fullscreen',
              'insertdatetime media table code help wordcount',
            ],
            toolbar:
              'undo redo | blocks | bold italic underline | \
              alignleft aligncenter alignright alignjustify | \
              bullist numlist | link image | code | help',
            content_style: 'body { font-family:Helvetica,Arial,sans-serif; font-size:14px }',
            automatic_uploads: true,
            images_upload_handler: handleImageUpload,
            paste_data_images: true, // Allow pasting images
            file_picker_types: 'image',
          }}
        />
      </div>

      <div class="form-row">
        <div class="form-group">
          <label for="display-order">Display Order</label>
          <input
            id="display-order"
            type="number"
            bind:value={displayOrder}
            min="0"
          />
          <small>Lower numbers appear first</small>
        </div>

        <div class="form-group checkbox-group">
          <label>
            <input type="checkbox" bind:checked={isPublished} />
            Published
          </label>
          <small>Visible to users</small>
        </div>

        <div class="form-group checkbox-group">
          <label>
            <input type="checkbox" bind:checked={isFeatured} />
            Featured
          </label>
          <small>Highlight prominently</small>
        </div>
      </div>

      <div class="form-row">
        <div class="form-group checkbox-group">
          <label>
            <input type="checkbox" bind:checked={showToTeachers} />
            Show to Teachers
          </label>
          <small>Visible to teachers</small>
        </div>

        <div class="form-group checkbox-group">
          <label>
            <input type="checkbox" bind:checked={showToStudents} />
            Show to Students
          </label>
          <small>Visible to students</small>
        </div>
      </div>

      <div class="form-actions">
        <button type="submit" disabled={isSubmitting} class="btn-primary">
          {#if isSubmitting}
            {editingId ? 'Updating...' : 'Creating...'}
          {:else}
            {editingId ? 'Update Section' : 'Create Section'}
          {/if}
        </button>
        
        {#if editingId}
          <button type="button" on:click={resetForm} class="btn-secondary">
            Cancel
          </button>
        {/if}
      </div>
    </form>
  </div>

  <!-- Sections List -->
  <div class="sections-list">
    <div class="list-header">
      <h3>Existing Sections ({sections.length})</h3>
      <button on:click={fetchSections} class="btn-refresh" disabled={loading}>
        {loading ? 'Loading...' : 'Refresh'}
      </button>
    </div>

    {#if loading && sections.length === 0}
      <div class="loading">Loading sections...</div>
    {:else if sections.length === 0}
      <div class="empty">No sections found. Create your first section above.</div>
    {:else}
      <div class="sections-table">
        <table>
          <thead>
            <tr>
              <th>Topic</th>
              <th>Order</th>
              <th>Status</th>
              <th>Views</th>
              <th>Created</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {#each sections as section}
              <tr>
                <td class="topic-cell">
                  <strong>{section.topic}</strong>
                  {#if section.is_featured}
                    <span class="badge featured">Featured</span>
                  {/if}
                </td>
                <td>{section.display_order}</td>
                <td>
                  {#if section.is_published}
                    <span class="badge published">Published</span>
                  {:else}
                    <span class="badge draft">Draft</span>
                  {/if}
                </td>
                <td>{section.view_count || 0}</td>
                <td class="date-cell">{formatDate(section.created_at)}</td>
                <td class="actions-cell">
                  <button 
                    on:click={() => loadSectionForEdit(section)} 
                    class="btn-edit"
                    title="Edit"
                  >
                    Edit
                  </button>
                  <button 
                    on:click={() => deleteSection(section.id)} 
                    class="btn-delete"
                    title="Delete"
                  >
                    Delete
                  </button>
                </td>
              </tr>
            {/each}
          </tbody>
        </table>
      </div>
    {/if}
  </div>
</div>

<style>
  .information-section-component {
    max-width: 100%;
    padding: 20px;
  }

  .information-section-component h2 {
    color: #fab45a;
    margin-bottom: 10px;
  }

  .description {
    color: #666;
    margin-bottom: 30px;
    font-size: 1em;
  }

  .message {
    padding: 12px 16px;
    border-radius: 6px;
    margin-bottom: 20px;
    font-weight: 500;
  }

  .message.success {
    background-color: #d4edda;
    color: #155724;
    border: 1px solid #c3e6cb;
  }

  .message.error {
    background-color: #f8d7da;
    color: #721c24;
    border: 1px solid #f5c6cb;
  }

  .section-form {
    background: white;
    padding: 25px;
    border-radius: 8px;
    box-shadow: 0px 0px 1px rgba(40, 41, 61, 0.08), 0px 0.5px 2px rgba(96, 97, 112, 0.16);
    margin-bottom: 30px;
  }

  .section-form h3 {
    margin-top: 0;
    margin-bottom: 20px;
    color: #333;
  }

  .form-group {
    margin-bottom: 20px;
  }

  .form-group label {
    display: block;
    margin-bottom: 8px;
    font-weight: 500;
    color: #333;
  }

  .form-group input[type="text"],
  .form-group input[type="number"] {
    width: 100%;
    padding: 10px;
    border: 2px solid #ddd;
    border-radius: 6px;
    font-size: 14px;
    box-sizing: border-box;
  }

  .form-group input[type="text"]:focus,
  .form-group input[type="number"]:focus {
    outline: none;
    border-color: #fab45a;
    box-shadow: 0 0 0 3px rgba(250, 180, 90, 0.1);
  }

  .form-group small {
    display: block;
    margin-top: 5px;
    color: #666;
    font-size: 12px;
  }

  .form-row {
    display: flex;
    gap: 20px;
    flex-wrap: wrap;
  }

  .form-row .form-group {
    flex: 1;
    min-width: 150px;
  }

  .checkbox-group {
    display: flex;
    flex-direction: column;
    justify-content: flex-end;
  }

  .checkbox-group label {
    display: flex;
    align-items: center;
    gap: 8px;
    cursor: pointer;
  }

  .checkbox-group input[type="checkbox"] {
    width: auto;
    cursor: pointer;
  }

  .form-actions {
    display: flex;
    gap: 10px;
    margin-top: 25px;
  }

  button {
    padding: 10px 20px;
    border: none;
    border-radius: 6px;
    font-size: 14px;
    font-weight: 500;
    cursor: pointer;
    transition: all 0.2s;
  }

  button:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }

  .btn-primary {
    background: #fab45a;
    color: black;
  }

  .btn-primary:hover:not(:disabled) {
    background: #f9a845;
  }

  .btn-secondary {
    background: #6c757d;
    color: white;
  }

  .btn-secondary:hover:not(:disabled) {
    background: #5a6268;
  }

  .btn-refresh {
    background: #007bff;
    color: white;
    padding: 8px 16px;
    font-size: 13px;
  }

  .btn-refresh:hover:not(:disabled) {
    background: #0056b3;
  }

  .sections-list {
    background: white;
    padding: 25px;
    border-radius: 8px;
    box-shadow: 0px 0px 1px rgba(40, 41, 61, 0.08), 0px 0.5px 2px rgba(96, 97, 112, 0.16);
  }

  .list-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 20px;
  }

  .list-header h3 {
    margin: 0;
    color: #333;
  }

  .loading,
  .empty {
    text-align: center;
    padding: 40px;
    color: #666;
  }

  .sections-table {
    overflow-x: auto;
  }

  table {
    width: 100%;
    border-collapse: collapse;
  }

  thead {
    background-color: #f8f9fa;
  }

  th {
    padding: 12px;
    text-align: left;
    font-weight: 600;
    color: #333;
    border-bottom: 2px solid #dee2e6;
  }

  td {
    padding: 12px;
    border-bottom: 1px solid #dee2e6;
    color: #555;
  }

  tr:hover {
    background-color: #f8f9fa;
  }

  .topic-cell {
    font-weight: 500;
  }

  .date-cell {
    font-size: 13px;
    color: #666;
  }

  .actions-cell {
    display: flex;
    gap: 8px;
  }

  .btn-edit {
    background: #17a2b8;
    color: white;
    padding: 6px 12px;
    font-size: 13px;
  }

  .btn-edit:hover {
    background: #138496;
  }

  .btn-delete {
    background: #dc3545;
    color: white;
    padding: 6px 12px;
    font-size: 13px;
  }

  .btn-delete:hover {
    background: #c82333;
  }

  .badge {
    display: inline-block;
    padding: 4px 8px;
    border-radius: 4px;
    font-size: 11px;
    font-weight: 600;
    margin-left: 8px;
  }

  .badge.published {
    background-color: #d4edda;
    color: #155724;
  }

  .badge.draft {
    background-color: #fff3cd;
    color: #856404;
  }

  .badge.featured {
    background-color: #d1ecf1;
    color: #0c5460;
  }
</style>

